/**
 * AssetImportExportPage.jsx
 *
 * Dedicated Import / Export page for the Asset Management module.
 *
 * Tabs:
 *   Import — drag-and-drop CSV upload, full client-side validation, preview
 *             table, overwrite option (testing only), POST to /api/AssetManagement/import
 *   Export  — Phase 2 stub
 *
 * Import flow:
 *   1. User drops / selects a .csv file.
 *   2. Client CSV parser reads the header row and all data rows.
 *   3. Validator checks every required field, Yes/No values, DD/MM/YYYY dates,
 *      numeric purchase cost, status enum, and property name resolution against
 *      the user's accessible properties list.
 *   4. Errors  → error table showing row #, column name, and issue description.
 *   5. No errors → preview table of all assets ready to import.
 *   6. User selects a default category (required for the Asset model),
 *      optionally ticks "Overwrite existing" (test-only), then clicks Save.
 *   7. POST to /api/AssetManagement/import → toast + success banner.
 *
 * Template format (professional standard):
 *   Row 1  — Column headers; required fields suffixed with " *"; format hints
 *             in parentheses (e.g. "Active * (Yes/No)").  These hints are
 *             stripped by normalizeHeader() so the parser still maps the column.
 *   Row 2  — Illustrative sample row.  Users are instructed to delete it before
 *             importing.  This mirrors the industry convention used by Salesforce,
 *             HubSpot, Xero, and Sage CSV templates.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-toastify';
import {
    FaUpload, FaDownload, FaFileAlt, FaArrowLeft,
    FaCheckCircle, FaExclamationTriangle, FaSpinner,
    FaTimes, FaSave, FaInfoCircle,
    FaFileImport, FaFileExport
} from 'react-icons/fa';
import * as Sentry from '@sentry/react';
import '@/styles/AssetImportExportStyles.scss';
import * as XLSX from 'xlsx';

// ── Column mapping ────────────────────────────────────────────────────────────
////
// Maps the normalised header string (lower-case, asterisks and parenthetical
// hints removed, trimmed) to the internal field key stored on each parsed row.
const COLUMN_MAP = {
    'asset tag': 'assetTag',
    'name': 'name',
    'description': 'description',
    'serial number': 'serialNumber',
    'active': 'isActive',
    'new': 'isNew',
    'manufacturer': 'manufacturer',
    'model': 'model',
    'property': 'propertyName',
    'location': 'location',
    'status': 'status',
    'belongs to tenant': 'belongsToTenant',
    'commission date': 'commissionDate',
    'installation date': 'installationDate',
    'requires servicing': 'requiresServicing',
    'under repair': 'isUnderRepair',
    'last service date': 'lastServiceDate',
    'next service date': 'nextServiceDate',
    'purchase cost': 'purchaseCost',
    'purchase cost includes vat': 'purchaseCostIncludesVat',
    'notes': 'notes',
    'has warranty': 'hasWarranty',
    'warranty expiry date': 'warrantyExpiryDate',
    'warranty notes': 'warrantyNotes',
    'asset count': 'quantity',
};

// Internal field keys that MUST be present and non-empty in every row.
const REQUIRED_FIELDS = new Set([
    'name', 'isActive', 'isNew', 'propertyName', 'status',
    'belongsToTenant', 'requiresServicing', 'isUnderRepair',
    'purchaseCostIncludesVat', 'hasWarranty',
]);

// All Yes/No boolean fields with their display labels.
const YES_NO_FIELDS = [
    { key: 'isActive', label: 'Active' },
    { key: 'isNew', label: 'New' },
    { key: 'belongsToTenant', label: 'Belongs to Tenant' },
    { key: 'requiresServicing', label: 'Requires Servicing' },
    { key: 'isUnderRepair', label: 'Under Repair' },
    { key: 'purchaseCostIncludesVat', label: 'Purchase Cost Includes Vat' },
    { key: 'hasWarranty', label: 'Has Warranty' },
];

// Date fields — all must follow DD/MM/YYYY when a value is supplied.
const DATE_FIELDS = [
    { key: 'commissionDate', label: 'Commission Date' },
    { key: 'installationDate', label: 'Installation Date' },
    { key: 'lastServiceDate', label: 'Last Service Date' },
    { key: 'nextServiceDate', label: 'Next Service Date' },
    { key: 'warrantyExpiryDate', label: 'Warranty Expiry Date' },
];

// Accepted status values (normalised — spaces removed, lower-cased).
const VALID_STATUSES_NORM = ['active', 'inactive', 'underrepair', 'decommissioned'];

// Maps normalised status → canonical casing expected by the API.
const STATUS_CANONICAL = {
    active: 'Active',
    inactive: 'Inactive',
    underrepair: 'UnderRepair',
    decommissioned: 'Decommissioned',
};

// ── Template definition ───────────────────────────────────────────────────────
//
// IMPORT_COLUMNS drives both the downloadTemplate Excel builder and the
// parser's column-mapping logic.  Single source of truth so that adding or
// renaming a column only needs one edit here.
const IMPORT_COLUMNS = [
    {
        header: 'Asset Tag',
        required: false,
        type: 'Text (max 100 chars)',
        allowed: 'Any text',
        example: 'TAG-001',
        hint: 'Optional',
        notes: 'Physical barcode, QR code, or tag reference. Leave blank if not applicable.',
    },
    {
        header: 'Name',
        required: true,
        type: 'Text (max 300 chars)',
        allowed: 'Any text',
        example: 'Main Escalator North Wing',
        hint: 'REQUIRED  •  Text, max 300 chars',
        notes: 'The asset display name shown throughout the system.',
    },
    {
        header: 'Description',
        required: false,
        type: 'Text (max 4 000 chars)',
        allowed: 'Any text',
        example: 'Primary escalator serving the north wing food court',
        hint: 'Optional  •  Text',
        notes: 'Detailed description of the asset, its purpose, and any relevant context.',
    },
    {
        header: 'Serial Number',
        required: false,
        type: 'Text (max 200 chars)',
        allowed: 'Any text',
        example: 'SN-ESC-NW-01',
        hint: 'Optional  •  Text',
        notes: "Manufacturer's serial number for warranty and service tracking.",
    },
    {
        header: 'Active',
        required: true,
        type: 'Yes / No',
        allowed: 'Yes  or  No',
        example: 'Yes',
        hint: 'REQUIRED  •  Yes or No',
        notes: 'Whether this asset record is active. Inactive assets are hidden by default.',
    },
    {
        header: 'New',
        required: true,
        type: 'Yes / No',
        allowed: 'Yes  or  No',
        example: 'No',
        hint: 'REQUIRED  •  Yes or No',
        notes: 'Mark Yes if the asset is new and pending commissioning documentation.',
    },
    {
        header: 'Manufacturer',
        required: false,
        type: 'Text (max 200 chars)',
        allowed: 'Any text',
        example: 'Otis',
        hint: 'Optional  •  Text',
        notes: 'Make or manufacturer of the asset (e.g. Otis, Carrier, Siemens).',
    },
    {
        header: 'Model',
        required: false,
        type: 'Text (max 200 chars)',
        allowed: 'Any text',
        example: 'XO-400',
        hint: 'Optional  •  Text',
        notes: 'Model number or model name from the manufacturer.',
    },
    {
        header: 'Property',
        required: true,
        type: 'Text — must match the system exactly',
        allowed: 'Exact property name (case-insensitive)',
        example: 'My Shopping Mall',
        hint: 'REQUIRED  •  Must match a property name in the system',
        notes: 'The property where this asset is located. Go to Asset Management and check the Property filter for valid names.',
    },
    {
        header: 'Location',
        required: false,
        type: 'Text (max 500 chars)',
        allowed: 'Any text',
        example: 'Level 2, North Wing',
        hint: 'Optional  •  Text',
        notes: 'Physical location within the property (e.g. Plant Room B, Roof Level).',
    },
    {
        header: 'Status',
        required: true,
        type: 'Fixed list',
        allowed: 'Active  |  Inactive  |  UnderRepair  |  Decommissioned',
        example: 'Active',
        hint: 'REQUIRED  •  Active / Inactive / UnderRepair / Decommissioned',
        notes: 'Current operational status. "UnderRepair" must be typed exactly — no spaces.',
    },
    {
        header: 'Belongs to Tenant',
        required: true,
        type: 'Yes / No',
        allowed: 'Yes  or  No',
        example: 'No',
        hint: 'REQUIRED  •  Yes or No',
        notes: 'Mark Yes if this asset belongs to a tenant rather than the property.',
    },
    {
        header: 'Commission Date',
        required: false,
        type: 'Date',
        allowed: 'DD/MM/YYYY',
        example: '15/01/2023',
        hint: 'Optional  •  DD/MM/YYYY',
        notes: 'Date the asset was inspected and formally commissioned for use.',
    },
    {
        header: 'Installation Date',
        required: false,
        type: 'Date',
        allowed: 'DD/MM/YYYY',
        example: '10/01/2023',
        hint: 'Optional  •  DD/MM/YYYY',
        notes: 'Date the asset was physically installed on site.',
    },
    {
        header: 'Requires Servicing',
        required: true,
        type: 'Yes / No',
        allowed: 'Yes  or  No',
        example: 'Yes',
        hint: 'REQUIRED  •  Yes or No',
        notes: 'If Yes, service reminders and action items will be generated for this asset.',
    },
    {
        header: 'Under Repair',
        required: true,
        type: 'Yes / No',
        allowed: 'Yes  or  No',
        example: 'No',
        hint: 'REQUIRED  •  Yes or No',
        notes: 'Mark Yes if the asset is currently checked in for Repairs & Maintenance.',
    },
    {
        header: 'Last Service Date',
        required: false,
        type: 'Date',
        allowed: 'DD/MM/YYYY',
        example: '15/01/2024',
        hint: 'Optional  •  DD/MM/YYYY',
        notes: 'Date of the most recently completed service.',
    },
    {
        header: 'Next Service Date',
        required: false,
        type: 'Date',
        allowed: 'DD/MM/YYYY',
        example: '15/07/2024',
        hint: 'Optional  •  DD/MM/YYYY',
        notes: 'Override for the next service due date. Leave blank to let the system calculate it from the service interval.',
    },
    {
        header: 'Purchase Cost (R)',
        required: false,
        type: 'Number (max 2 decimal places)',
        allowed: 'Positive number, e.g. 45000.00',
        example: '45000.00',
        hint: 'Optional  •  Positive number, max 2 decimals (e.g. 45000.00)',
        notes: 'Original acquisition or purchase cost of the asset.',
    },
    {
        header: 'Purchase Cost Includes Vat',
        required: true,
        type: 'Yes / No',
        allowed: 'Yes  or  No',
        example: 'No',
        hint: 'REQUIRED  •  Yes or No',
        notes: 'Whether the Purchase Cost figure includes VAT. Most asset costs are captured excl. VAT.',
    },
    {
        header: 'Notes',
        required: false,
        type: 'Text (max 4 000 chars)',
        allowed: 'Any text',
        example: 'Annual maintenance contract with Otis',
        hint: 'Optional  •  Text',
        notes: 'General notes about the asset — visible to all users with access.',
    },
    {
        header: 'Has Warranty',
        required: true,
        type: 'Yes / No',
        allowed: 'Yes  or  No',
        example: 'Yes',
        hint: 'REQUIRED  •  Yes or No',
        notes: 'Mark Yes if the asset has an active warranty. If Yes, fill in Warranty Expiry Date.',
    },
    {
        header: 'Warranty Expiry Date',
        required: false,
        type: 'Date',
        allowed: 'DD/MM/YYYY',
        example: '15/01/2026',
        hint: 'Optional  •  DD/MM/YYYY',
        notes: 'Date the warranty expires. Leave blank if Has Warranty is No.',
    },
    {
        header: 'Warranty Notes',
        required: false,
        type: 'Text (max 2 000 chars)',
        allowed: 'Any text',
        example: 'Full parts and labour warranty for 3 years',
        hint: 'Optional  •  Text',
        notes: 'Notes about warranty coverage, exclusions, or provider details.',
    },
    {
        header: 'Asset Count',
        required: false,
        type: 'Number (positive whole number)',
        allowed: 'Positive whole number, e.g. 1, 12',
        example: '1',
        hint: 'Optional  •  Positive whole number (default: 1)',
        notes: 'Number of identical items this record represents. Leave blank or enter 1 for individually tracked assets (e.g. escalators). Use >1 for fungible items (e.g. 12 identical fire extinguishers).',
    },
];

// Marker placed in the first cell of the hints row so the parser
// can detect and skip it whether the file is uploaded as .xlsx or .csv.
const HINTS_ROW_MARKER = '## FORMAT GUIDE - DO NOT IMPORT ##';

// ── Template download (Excel .xlsx) ──────────────────────────────────────────
const downloadTemplate = () => {
    const wb = XLSX.utils.book_new();

    // ── Sheet 1: Instructions ─────────────────────────────────────────────
    const today = new Date().toLocaleDateString('en-ZA', {
        day: '2-digit', month: 'long', year: 'numeric',
    });

    const exampleDataRow = IMPORT_COLUMNS.map(c => c.example);
    const exampleHeaderRow = IMPORT_COLUMNS.map(c => c.required ? `${c.header} *` : c.header);

    const instructionRows = [
        // Title block
        ['CapTrac  \u2014  Asset Import Template'],
        [`Generated: ${today}   |   Fill in the "Asset Import" sheet, then upload this file (or save as .csv first).`],
        [''],

        // Step-by-step guide
        ['HOW TO USE THIS TEMPLATE'],
        ['Step 1', 'Open the "Asset Import" tab below. Row 1 is the header row \u2014 start entering your assets from Row 2 onward.'],
        ['Step 2', 'Columns marked with * (REQUIRED) must have a value in every row. Optional columns may be left blank.'],
        ['Step 3', 'Property names must exactly match the property names shown in the system (check the Asset Management page for valid names).'],
        ['Step 4', 'Dates must be in DD/MM/YYYY format (e.g. 15/01/2024). Yes/No columns accept only "Yes" or "No".'],
        ['Step 5', 'Status must be one of exactly: Active, Inactive, UnderRepair, Decommissioned.'],
        ['Step 6', 'Upload this file directly on the Import page \u2014 both .xlsx and .csv are accepted.'],
        [''],

        // Column reference
        ['COLUMN REFERENCE'],
        ['Column', 'Required?', 'Data Type', 'Allowed Values / Format', 'Example Value', 'Description / Notes'],
        ...IMPORT_COLUMNS.map(col => [
            col.required ? `${col.header} *` : col.header,
            col.required ? 'REQUIRED' : 'Optional',
            col.type,
            col.allowed,
            col.example,
            col.notes,
        ]),

        [''],

        // Example row — shows exactly what a filled-in row looks like
        ['EXAMPLE ROW'],
        exampleHeaderRow,
        exampleDataRow,
        ['DO NOT FILL IN THE ASSETS HERE, WORK IN THE "Asset Import" SHEET'],
    ];

    const instrWs = XLSX.utils.aoa_to_sheet(instructionRows);

    // Row count before the example section: 3 (title block) + 1 (HOW TO USE) + 7 (steps) + 1 (blank) + 1 (COLUMN REFERENCE) + 1 (col ref header) + IMPORT_COLUMNS.length + 1 (blank) = 15 + IMPORT_COLUMNS.length
    const exampleSectionStart = 13 + IMPORT_COLUMNS.length + 1; // 0-based row index of "EXAMPLE ROW"

    instrWs['!cols'] = [
        { wch: 30 },  // Col 0  — Column name / Asset Tag
        { wch: 32 },  // Col 1  — Required? / Name
        { wch: 36 },  // Col 2  — Data Type / Description
        { wch: 50 },  // Col 3  — Allowed Values / Serial Number
        { wch: 28 },  // Col 4  — Example Value / Active
        { wch: 55 },  // Col 5  — Notes / New
        { wch: 20 },  // Col 6  — Manufacturer
        { wch: 20 },  // Col 7  — Model
        { wch: 30 },  // Col 8  — Property
        { wch: 26 },  // Col 9  — Location
        { wch: 20 },  // Col 10 — Status
        { wch: 20 },  // Col 11 — Belongs to Tenant
        { wch: 20 },  // Col 12 — Commission Date
        { wch: 20 },  // Col 13 — Installation Date
        { wch: 20 },  // Col 14 — Requires Servicing
        { wch: 14 },  // Col 15 — Under Repair
        { wch: 20 },  // Col 16 — Last Service Date
        { wch: 20 },  // Col 17 — Next Service Date
        { wch: 22 },  // Col 18 — Purchase Cost
        { wch: 28 },  // Col 19 — Purchase Cost Includes Vat
        { wch: 36 },  // Col 20 — Notes
        { wch: 14 },  // Col 21 — Has Warranty
        { wch: 24 },  // Col 22 — Warranty Expiry Date
        { wch: 30 },  // Col 23 — Warranty Notes
        { wch: 14 },  // Col 24 — Asset Count
    ];

    instrWs['!merges'] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 5 } },                                          // Title
        { s: { r: 1, c: 0 }, e: { r: 1, c: 5 } },                                          // Generated date
        { s: { r: 3, c: 0 }, e: { r: 3, c: 5 } },                                          // HOW TO USE
        { s: { r: 12, c: 0 }, e: { r: 12, c: 5 } },                                        // COLUMN REFERENCE
        { s: { r: exampleSectionStart, c: 0 }, e: { r: exampleSectionStart, c: 5 } },       // EXAMPLE ROW
    ];

    XLSX.utils.book_append_sheet(wb, instrWs, 'Instructions');

    // -- Sheet 2: Asset Import — header row only, completely clean ----------------
    const headerRow = IMPORT_COLUMNS.map(c => c.required ? `${c.header} *` : c.header);

    const importWs = XLSX.utils.aoa_to_sheet([headerRow]);

    // Column widths
    importWs['!cols'] = [
        { wch: 14 },  // Asset Tag
        { wch: 32 },  // Name
        { wch: 36 },  // Description
        { wch: 18 },  // Serial Number
        { wch: 10 },  // Active
        { wch: 10 },  // New
        { wch: 20 },  // Manufacturer
        { wch: 20 },  // Model
        { wch: 30 },  // Property
        { wch: 26 },  // Location
        { wch: 20 },  // Status
        { wch: 20 },  // Belongs to Tenant
        { wch: 20 },  // Commission Date
        { wch: 20 },  // Installation Date
        { wch: 20 },  // Requires Servicing
        { wch: 14 },  // Under Repair
        { wch: 20 },  // Last Service Date
        { wch: 20 },  // Next Service Date
        { wch: 22 },  // Purchase Cost
        { wch: 28 },  // Purchase Cost Includes Vat
        { wch: 36 },  // Notes
        { wch: 14 },  // Has Warranty
        { wch: 24 },  // Warranty Expiry Date
        { wch: 30 },  // Warranty Notes
        { wch: 14 },  // Asset Count
    ];

    // Freeze the top 2 rows so the header and hints stay visible while scrolling.
    importWs['!freeze'] = { xSplit: 0, ySplit: 2 };

    // Auto-filter on the header row.
    const lastCol = XLSX.utils.encode_col(IMPORT_COLUMNS.length - 1);
    importWs['!autofilter'] = { ref: `A1:${lastCol}1` };

    XLSX.utils.book_append_sheet(wb, importWs, 'Asset Import');

    // Download via Blob — more reliable in browsers than XLSX.writeFile.
    const wbOut = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbOut], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'CapTrac_Asset_Import_Template.xlsx';
    a.click();
    URL.revokeObjectURL(url);

    toast.info('Template downloaded. Start entering assets from Row 2 on the "Asset Import" sheet.');
};

// ── Pure CSV parser (RFC 4180) ────────────────────────────────────────────────
//
// Handles quoted fields, embedded commas, escaped double-quotes (""),
// and both \r\n and bare \n line endings.  Strips a leading UTF-8 BOM.
// Returns an array-of-arrays; trailing completely-empty rows are dropped.
function parseCSV(rawText) {
    // Strip BOM if present (some Excel CSV exports include it).
    const text = rawText.charCodeAt(0) === 0xFEFF ? rawText.slice(1) : rawText;

    const rows = [];
    let row = [];
    let field = '';
    let inQuotes = false;
    const n = text.length;

    for (let i = 0; i < n; i++) {
        const ch = text[i];
        if (inQuotes) {
            if (ch === '"') {
                if (text[i + 1] === '"') { field += '"'; i++; }
                else inQuotes = false;
            } else {
                field += ch;
            }
        } else {
            if (ch === '"') {
                inQuotes = true;
            } else if (ch === ',') {
                row.push(field.trim());
                field = '';
            } else if (ch === '\r' && text[i + 1] === '\n') {
                row.push(field.trim());
                rows.push(row);
                row = []; field = ''; i++;
            } else if (ch === '\n') {
                row.push(field.trim());
                rows.push(row);
                row = []; field = '';
            } else {
                field += ch;
            }
        }
    }
    if (row.length > 0 || field !== '') {
        row.push(field.trim());
        rows.push(row);
    }
    // Drop trailing blank rows.
    while (rows.length > 0 && rows[rows.length - 1].every(c => c === ''))
        rows.pop();
    return rows;
}

// Strips required-field markers (* ) and parenthetical format hints from a
// header string, then lower-cases and trims it for use as a COLUMN_MAP key.
function normalizeHeader(raw) {
    return raw
        .toLowerCase()
        .replace(/\s*\*\s*/g, '')
        .replace(/\s*\([^)]*\)/g, '')
        .trim();
}

// Converts a DD/MM/YYYY string to ISO YYYY-MM-DD for the API.
// Returns null when the value is empty.
function ddMmYyyyToIso(val) {
    if (!val || !val.trim()) return null;
    const parts = val.trim().split('/');
    if (parts.length !== 3) return null;
    const [dd, mm, yyyy] = parts;
    return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
}

// ── Component ─────────────────────────────────────────────────────────────────
const AssetImportExportPage = () => {
    const { apiFetch } = useAuth();
    const navigate = useNavigate();

    // ── UI state ────────────────────────────────────────────────────────────
    const [activeTab, setActiveTab] = useState('import');

    // ── Reference data ───────────────────────────────────────────────────────
    const [properties, setProperties] = useState([]);
    const [categories, setCategories] = useState([]);
    const [refLoading, setRefLoading] = useState(true);

    // ── Import configuration ─────────────────────────────────────────────────
    const [defaultCategoryId, setDefaultCategoryId] = useState('');
    const [overwriteExisting, setOverwriteExisting] = useState(false);

    // ── File / parse state ────────────────────────────────────────────────────
    // parseState: 'idle' | 'parsing' | 'errors' | 'preview'
    const [isDragging, setIsDragging] = useState(false);
    const [file, setFile] = useState(null);
    const [parseState, setParseState] = useState('idle');
    const [headerErrors, setHeaderErrors] = useState([]);
    const [validationErrors, setValidationErrors] = useState([]);
    const [parsedAssets, setParsedAssets] = useState([]);
    const fileInputRef = useRef(null);

    // ── Import action state ───────────────────────────────────────────────────
    const [importing, setImporting] = useState(false);
    const [importResult, setImportResult] = useState(null);

    // ── Reference data fetch ─────────────────────────────────────────────────
    const fetchReferenceData = useCallback(async () => {
        setRefLoading(true);
        try {
            const [propRes, catRes] = await Promise.all([
                apiFetch('/api/AssetManagement/properties'),
                apiFetch('/api/AssetManagement/categories'),
            ]);
            const propData = await propRes.json();
            const catData = await catRes.json();

            if (propData.success && Array.isArray(propData.data)) {
                setProperties(
                    [...propData.data].sort((a, b) =>
                        (a.propertyName || '').localeCompare(b.propertyName || ''))
                );
            }
            if (catData.success && Array.isArray(catData.data)) {
                const active = catData.data.filter(c => c.isActive);
                setCategories(
                    [...active].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                );
                if (active.length > 0 && !defaultCategoryId) {
                    setDefaultCategoryId(String(active[0].assetCategoryId));
                }
            }
        } catch (err) {
            Sentry.captureException(err);
            toast.error('Failed to load reference data. Please refresh the page.');
        } finally {
            setRefLoading(false);
        }
    }, [apiFetch]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        fetchReferenceData();
    }, [fetchReferenceData]);

    // ── CSV / XLSX processing ─────────────────────────────────────────────────
    //
    // Shared validation logic called after parsing — receives an array-of-arrays
    // where row[0] is the header row and row[1+] are data rows.
    const validateRows = useCallback((rows) => {
        try {
            if (rows.length < 1) {
                setHeaderErrors(['The file appears to be empty.']);
                setParseState('errors');
                return;
            }

            // ── Map headers ──────────────────────────────────────────────────
            const rawHeaders = rows[0];
            const colIndexMap = {};
            rawHeaders.forEach((raw, idx) => {
                const normalized = normalizeHeader(String(raw ?? ''));
                const fieldKey = COLUMN_MAP[normalized];
                if (fieldKey) colIndexMap[fieldKey] = idx;
            });

            const missingCols = [];
            for (const field of REQUIRED_FIELDS) {
                if (colIndexMap[field] === undefined) {
                    const displayLabel =
                        Object.entries(COLUMN_MAP).find(([, v]) => v === field)?.[0] ?? field;
                    missingCols.push(
                        `Required column "${displayLabel}" is missing. Check the column name matches the template exactly.`
                    );
                }
            }
            if (missingCols.length > 0) {
                setHeaderErrors(missingCols);
                setParseState('errors');
                return;
            }

            // ── Filter data rows ─────────────────────────────────────────────
            // Skip rows that are entirely blank, or that contain the hints-row marker.
            const dataRows = rows.slice(1).filter(r => {
                const firstCell = String(r[0] ?? '').trim();
                if (firstCell.startsWith('##')) return false; // hints row marker
                return r.some(c => String(c ?? '').trim() !== '');
            });

            if (dataRows.length === 0) {
                setHeaderErrors([
                    'The file has no data rows. Add at least one asset row below the header ' +
                    '(Row 2 if you are using the template).',
                ]);
                setParseState('errors');
                return;
            }

            // ── Row-level validation ─────────────────────────────────────────
            const errors = [];
            const valid = [];

            dataRows.forEach((rowArr, idx) => {
                const rowNum = idx + 2;
                const row = {};
                Object.entries(colIndexMap).forEach(([fieldKey, colIdx]) => {
                    row[fieldKey] = String(rowArr[colIdx] ?? '').trim();
                });

                let rowErrorCount = 0;
                const addErr = (column, issue) => {
                    errors.push({ row: rowNum, column, issue });
                    rowErrorCount++;
                };

                // Required text: Name
                if (!row.name) addErr('Name', 'Name is required.');

                // Required text: Property — resolve against known list
                // Required text: Property — resolve against known list using tiered match:
                //   1. Exact (case-insensitive)
                //   2. Property name starts with the input
                //   3. Property name contains the input
                // Ambiguous (multiple matches) is treated as not found.
                if (!row.propertyName) {
                    addErr('Property', 'Property is required.');
                } else {
                    const input = row.propertyName.toLowerCase();

                    const resolveProperty = () => {
                        // Tier 1 — exact
                        let matches = properties.filter(
                            p => (p.propertyName || '').toLowerCase() === input
                        );
                        if (matches.length === 1) return matches[0];

                        // Tier 2 — starts with
                        matches = properties.filter(
                            p => (p.propertyName || '').toLowerCase().startsWith(input)
                        );
                        if (matches.length === 1) return matches[0];

                        // Tier 3 — contains
                        matches = properties.filter(
                            p => (p.propertyName || '').toLowerCase().includes(input)
                        );
                        if (matches.length === 1) return matches[0];

                        return null;
                    };

                    const found = resolveProperty();
                    if (!found) {
                        addErr(
                            'Property',
                            `Property "${row.propertyName}" was not found or matched multiple properties. ` +
                            'Use a unique part of the name (case-insensitive).'
                        );
                    }
                }

                // Yes/No fields
                for (const { key, label } of YES_NO_FIELDS) {
                    const val = (row[key] || '').toLowerCase();
                    if (REQUIRED_FIELDS.has(key) && !val) {
                        addErr(label, `${label} is required. Enter "Yes" or "No".`);
                    } else if (val && val !== 'yes' && val !== 'no') {
                        addErr(label, `Invalid value "${row[key]}". Must be "Yes" or "No".`);
                    }
                }

                // Status
                const statusNorm = (row.status || '').toLowerCase().replace(/\s+/g, '');
                if (!row.status) {
                    addErr('Status', 'Status is required.');
                } else if (!VALID_STATUSES_NORM.includes(statusNorm)) {
                    addErr(
                        'Status',
                        `Invalid status "${row.status}". Must be one of: Active, Inactive, UnderRepair, Decommissioned.`
                    );
                }

                // Date fields
                for (const { key, label } of DATE_FIELDS) {
                    const val = row[key];
                    if (val) {
                        if (!/^\d{2}\/\d{2}\/\d{4}$/.test(val)) {
                            addErr(label, `Invalid date format "${val}". Must be DD/MM/YYYY.`);
                        } else {
                            const [dd, mm, yyyy] = val.split('/').map(Number);
                            const d = new Date(yyyy, mm - 1, dd);
                            if (
                                d.getFullYear() !== yyyy ||
                                d.getMonth() !== mm - 1 ||
                                d.getDate() !== dd
                            ) {
                                addErr(label, `"${val}" is not a valid calendar date.`);
                            }
                        }
                    }
                }

                // Purchase cost
                if (row.purchaseCost) {
                    const cleaned = row.purchaseCost.replace(/,/g, '');
                    const num = Number(cleaned);
                    if (isNaN(num) || num < 0) {
                        addErr(
                            'Purchase Cost',
                            `Invalid value "${row.purchaseCost}". Must be a positive number (e.g. 45000.00).`
                        );
                    } else if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) {
                        addErr(
                            'Purchase Cost',
                            `"${row.purchaseCost}" has more than 2 decimal places. Maximum is 2.`
                        );
                    }
                }

                // Asset Count (Quantity)
                if (row.quantity) {
                    const qty = Number(row.quantity);
                    if (!Number.isInteger(qty) || qty < 1) {
                        addErr(
                            'Asset Count',
                            `Invalid value "${row.quantity}". Must be a positive whole number (e.g. 1, 12).`,
                        );
                    }
                }

                if (rowErrorCount === 0) {
                    valid.push({
                        assetTag: row.assetTag || null,
                        name: row.name,
                        description: row.description || null,
                        serialNumber: row.serialNumber || null,
                        isActive: row.isActive.toLowerCase() === 'yes',
                        isNew: row.isNew.toLowerCase() === 'yes',
                        manufacturer: row.manufacturer || null,
                        model: row.model || null,
                        propertyName: row.propertyName,
                        location: row.location || null,
                        status: STATUS_CANONICAL[statusNorm] ?? row.status,
                        belongsToTenant: (row.belongsToTenant || '').toLowerCase() === 'yes',
                        commissionDate: ddMmYyyyToIso(row.commissionDate),
                        installationDate: ddMmYyyyToIso(row.installationDate),
                        requiresServicing: (row.requiresServicing || '').toLowerCase() === 'yes',
                        isUnderRepair: (row.isUnderRepair || '').toLowerCase() === 'yes',
                        lastServiceDate: ddMmYyyyToIso(row.lastServiceDate),
                        nextServiceDate: ddMmYyyyToIso(row.nextServiceDate),
                        purchaseCost: row.purchaseCost
                            ? parseFloat(row.purchaseCost.replace(/,/g, ''))
                            : null,
                        purchaseCostIncludesVat:
                            (row.purchaseCostIncludesVat || '').toLowerCase() === 'yes',
                        notes: row.notes || null,
                        hasWarranty: (row.hasWarranty || '').toLowerCase() === 'yes',
                        warrantyExpiryDate: ddMmYyyyToIso(row.warrantyExpiryDate),
                        warrantyNotes: row.warrantyNotes || null,
                        quantity: parseInt(row.quantity, 10) || 1,
                    });
                }
            });

            setValidationErrors(errors);
            setParsedAssets(valid);
            setParseState(errors.length > 0 ? 'errors' : 'preview');

        } catch (err) {
            Sentry.captureException(err);
            setHeaderErrors([
                'An unexpected error occurred while parsing the file. ' +
                'Ensure it is a valid CSV or Excel (.xlsx) file and try again.',
            ]);
            setParseState('errors');
        }
    }, [properties]);

    const processFile = useCallback((csvFile) => {
        if (!csvFile) return;
        setFile(csvFile);
        setParseState('parsing');
        setHeaderErrors([]);
        setValidationErrors([]);
        setParsedAssets([]);
        setImportResult(null);

        const ext = csvFile.name.toLowerCase().split('.').pop();

        if (ext === 'xlsx' || ext === 'xls') {
            // ── Excel path — use SheetJS ─────────────────────────────────
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const data = new Uint8Array(e.target.result);
                    const wb = XLSX.read(data, { type: 'array' });

                    // Prefer the "Asset Import" sheet (from our template);
                    // fall back to the first sheet for custom workbooks.
                    const sheetName = wb.SheetNames.includes('Asset Import')
                        ? 'Asset Import'
                        : wb.SheetNames[0];

                    const ws = wb.Sheets[sheetName];
                    const rows = XLSX.utils.sheet_to_json(ws, {
                        header: 1,
                        defval: '',
                        raw: false, // keep everything as strings
                    }).map(row => row.map(cell => String(cell ?? '').trim()));

                    validateRows(rows);
                } catch (err) {
                    Sentry.captureException(err);
                    setHeaderErrors([
                        'Could not read the Excel file. Ensure it is a valid .xlsx file and try again.',
                    ]);
                    setParseState('errors');
                }
            };
            reader.readAsArrayBuffer(csvFile);

        } else {
            // ── CSV path — plain text parser ─────────────────────────────
            const reader = new FileReader();
            reader.onload = (e) => {
                const rows = parseCSV(e.target.result);
                validateRows(rows);
            };
            reader.readAsText(csvFile, 'UTF-8');
        }
    }, [validateRows]);

    // ── Drag-and-drop handlers ────────────────────────────────────────────────
    const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
    const handleDragLeave = (e) => { e.preventDefault(); setIsDragging(false); };

    // Replace the existing handleDrop and handleFileInputChange:

    const ACCEPTED_EXTENSIONS = ['.csv', '.xlsx', '.xls'];

    const isAcceptedFile = (name) =>
        ACCEPTED_EXTENSIONS.some(ext => name.toLowerCase().endsWith(ext));

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const dropped = e.dataTransfer.files[0];
        if (!dropped) return;
        if (!isAcceptedFile(dropped.name)) {
            toast.warning('Only CSV or Excel (.xlsx) files are accepted.');
            return;
        }
        processFile(dropped);
    };

    const handleFileInputChange = (e) => {
        const selected = e.target.files[0];
        if (!selected) return;
        if (!isAcceptedFile(selected.name)) {
            toast.warning('Only CSV or Excel (.xlsx) files are accepted.');
            return;
        }
        processFile(selected);
    };

    const resetImport = () => {
        setFile(null);
        setParseState('idle');
        setHeaderErrors([]);
        setValidationErrors([]);
        setParsedAssets([]);
        setImportResult(null);
        setOverwriteExisting(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    // ── Save import ───────────────────────────────────────────────────────────
    const handleSaveImport = async () => {
        if (!defaultCategoryId) {
            toast.warning('Please select a Default Category before saving.');
            return;
        }
        if (parsedAssets.length === 0) {
            toast.warning('No valid assets to import.');
            return;
        }
        if (overwriteExisting) {
            const confirmed = window.confirm(
                `WARNING\n\n` +
                `This will permanently DELETE all existing assets for every property ` +
                `included in this import file and replace them with the ` +
                `${parsedAssets.length} assets from this CSV.\n\n` +
                `This action CANNOT be undone. Continue?`
            );
            if (!confirmed) return;
        }

        try {
            setImporting(true);
            const payload = {
                defaultAssetCategoryId: parseInt(defaultCategoryId, 10),
                overwriteExisting,
                rows: parsedAssets,
            };
            const res = await apiFetch('/api/AssetManagement/import', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            const data = await res.json();
            if (data.success) {
                const count = data.data?.count ?? parsedAssets.length;
                setImportResult({ count, message: data.message });
                resetImport();
                toast.success(data.message || `${count} assets imported successfully.`);
            } else {
                toast.error(data.message || 'Import failed. Please try again.');
            }
        } catch (err) {
            Sentry.captureException(err);
            toast.error('An error occurred during import. Please try again.');
        } finally {
            setImporting(false);
        }
    };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="aie-page">

            {/* Page Header */}
            <div className="aie-page-header">
                <button className="aie-back-btn" onClick={() => navigate('/operations/asset-management')}>
                    <FaArrowLeft /> Back to Asset Management
                </button>
                <h1><FaFileAlt /> Asset Import / Export</h1>
            </div>

            {/* Tabs */}
            <div className="aie-tabs">
                <button
                    className={`aie-tab ${activeTab === 'import' ? 'active' : ''}`}
                    onClick={() => setActiveTab('import')}
                >
                    <FaFileImport /> Import
                </button>
                <button
                    className={`aie-tab ${activeTab === 'export' ? 'active' : ''}`}
                    onClick={() => setActiveTab('export')}
                >
                    <FaFileExport /> Export
                </button>
            </div>

            <div className="aie-tab-content">

                {/* ══ IMPORT TAB ════════════════════════════════════════════ */}
                {activeTab === 'import' && (
                    <div className="aie-import">

                        {refLoading ? (
                            <div className="aie-loading">
                                <FaSpinner className="fa-spin" /> Loading reference data…
                            </div>
                        ) : (
                            <>
                                {/* Info banner + template download */}
                                <div className="aie-info-banner">
                                    <FaInfoCircle className="aie-info-icon" />
                                    <div className="aie-info-text">
                                        <strong>How to import:</strong> Download the template, fill in
                                        your assets (delete the sample row first), then upload the file.
                                        Columns marked <strong>*</strong> are required.
                                        Property names must match the system exactly.
                                    </div>
                                    <button className="aie-template-btn" onClick={downloadTemplate}>
                                        <FaDownload /> Download Template
                                    </button>
                                </div>

                                {/* Default category — shown above the drop zone so users
                                    configure it before selecting a file */}
                                <div className="aie-config-row">
                                    <label className="aie-label">
                                        Default Category
                                        <span className="aie-required"> *</span>
                                        <span className="aie-label-hint"> — applied to all imported assets</span>
                                    </label>
                                    <select
                                        className="aie-select"
                                        value={defaultCategoryId}
                                        onChange={(e) => setDefaultCategoryId(e.target.value)}
                                    >
                                        <option value="">— Select a category —</option>
                                        {categories.map(c => (
                                            <option key={c.assetCategoryId} value={c.assetCategoryId}>
                                                {c.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Success result banner */}
                                {importResult && (
                                    <div className="aie-success-banner">
                                        <FaCheckCircle />
                                        <span>
                                            {importResult.message ||
                                                `${importResult.count} assets imported successfully.`}
                                        </span>
                                        <button
                                            className="aie-dismiss-btn"
                                            onClick={() => setImportResult(null)}
                                        >
                                            <FaTimes />
                                        </button>
                                    </div>
                                )}

                                {/* Drop zone — hidden once a file is loaded */}
                                {parseState === 'idle' && (
                                    <div
                                        className={`aie-dropzone ${isDragging ? 'dragging' : ''}`}
                                        onDragOver={handleDragOver}
                                        onDragLeave={handleDragLeave}
                                        onDrop={handleDrop}
                                        onClick={() => fileInputRef.current?.click()}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
                                        aria-label="Upload CSV or Excel file"
                                    >
                                        <FaUpload className="aie-dz-icon" />
                                        <p className="aie-dz-title">Drag &amp; drop your file here</p>
                                        <p className="aie-dz-sub">or click to browse — .xlsx or .csv files</p>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept=".csv,.xlsx,.xls"
                                            style={{ display: 'none' }}
                                            onChange={handleFileInputChange}
                                        />
                                    </div>
                                )}

                                {/* Parsing spinner */}
                                {parseState === 'parsing' && (
                                    <div className="aie-loading">
                                        <FaSpinner className="fa-spin" />
                                        Validating &ldquo;{file?.name}&rdquo;…
                                    </div>
                                )}

                                {/* File info bar */}
                                {(parseState === 'errors' || parseState === 'preview') && file && (
                                    <div className="aie-file-bar">
                                        <FaFileAlt className="aie-file-icon" />
                                        <span className="aie-file-name">{file.name}</span>
                                        <span className="aie-file-size">
                                            ({(file.size / 1024).toFixed(1)} KB)
                                        </span>
                                        <button
                                            className="aie-remove-file-btn"
                                            onClick={resetImport}
                                            title="Remove file and start over"
                                        >
                                            <FaTimes /> Remove
                                        </button>
                                    </div>
                                )}

                                {/* Structural / header errors */}
                                {headerErrors.length > 0 && (
                                    <div className="aie-header-error-box">
                                        <FaExclamationTriangle />
                                        <div>
                                            <strong>File structure issues — fix and re-upload:</strong>
                                            <ul>
                                                {headerErrors.map((msg, i) => (
                                                    <li key={i}>{msg}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                )}

                                {/* Row-level validation error table */}
                                {parseState === 'errors' && validationErrors.length > 0 && (
                                    <div className="aie-validation-section">
                                        <div className="aie-validation-title">
                                            <FaExclamationTriangle className="aie-err-icon" />
                                            <h3>
                                                {validationErrors.length} validation issue
                                                {validationErrors.length !== 1 ? 's' : ''} found
                                                — correct the CSV and re-upload
                                            </h3>
                                        </div>
                                        <div className="aie-table-scroll">
                                            <table className="aie-errors-table">
                                                <thead>
                                                    <tr>
                                                        <th>Row</th>
                                                        <th>Column</th>
                                                        <th>Issue</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {validationErrors.map((err, i) => (
                                                        <tr key={i}>
                                                            <td className="aie-cell-row">{err.row}</td>
                                                            <td className="aie-cell-col">{err.column}</td>
                                                            <td>{err.issue}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                {/* Preview section — only visible when all rows pass */}
                                {parseState === 'preview' && parsedAssets.length > 0 && (
                                    <div className="aie-preview-section">
                                        <div className="aie-preview-title">
                                            <FaCheckCircle className="aie-ok-icon" />
                                            <h3>
                                                Validation passed —{' '}
                                                <strong>{parsedAssets.length}</strong> asset
                                                {parsedAssets.length !== 1 ? 's' : ''} ready to import
                                            </h3>
                                        </div>

                                        {/* Overwrite toggle (for testing only) */}
                                        <div className="aie-overwrite-row">
                                            <label className="aie-checkbox-label">
                                                <input
                                                    type="checkbox"
                                                    checked={overwriteExisting}
                                                    onChange={(e) => setOverwriteExisting(e.target.checked)}
                                                />
                                                <span>Overwrite existing assets for imported properties</span>
                                                <span className="aie-test-badge">For Testing Only</span>
                                            </label>
                                            {overwriteExisting && (
                                                <div className="aie-overwrite-warning">
                                                    <FaExclamationTriangle />
                                                    All current assets for the properties in this file will be
                                                    permanently deleted and replaced with the data from this CSV.
                                                    This cannot be undone.
                                                </div>
                                            )}
                                        </div>

                                        {/* Asset preview table */}
                                        <div className="aie-table-scroll">
                                            <table className="aie-preview-table">
                                                <thead>
                                                    <tr>
                                                        <th>#</th>
                                                        <th>Name</th>
                                                        <th>Asset Tag</th>
                                                        <th>Property</th>
                                                        <th>Status</th>
                                                        <th>Active</th>
                                                        <th>Requires Servicing</th>
                                                        <th>Has Warranty</th>
                                                        <th>Purchase Cost</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {parsedAssets.map((a, i) => (
                                                        <tr key={i}>
                                                            <td className="aie-cell-num">{i + 1}</td>
                                                            <td className="aie-cell-name">{a.name}</td>
                                                            <td>{a.assetTag || '—'}</td>
                                                            <td>{a.propertyName}</td>
                                                            <td>
                                                                <span className={`aie-status-badge aie-s-${(a.status || '').toLowerCase()}`}>
                                                                    {a.status}
                                                                </span>
                                                            </td>
                                                            <td>{a.isActive ? 'Yes' : 'No'}</td>
                                                            <td>{a.requiresServicing ? 'Yes' : 'No'}</td>
                                                            <td>{a.hasWarranty ? 'Yes' : 'No'}</td>
                                                            <td>
                                                                {a.purchaseCost != null
                                                                    ? `R ${a.purchaseCost.toFixed(2)}`
                                                                    : '—'}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>

                                        {/* Action buttons */}
                                        <div className="aie-action-row">
                                            <button
                                                className="aie-save-btn"
                                                onClick={handleSaveImport}
                                                disabled={importing || !defaultCategoryId}
                                            >
                                                {importing
                                                    ? <><FaSpinner className="fa-spin" /> Importing…</>
                                                    : <><FaSave /> Save Import ({parsedAssets.length} asset{parsedAssets.length !== 1 ? 's' : ''})</>}
                                            </button>
                                            <button
                                                className="aie-cancel-btn"
                                                onClick={resetImport}
                                                disabled={importing}
                                            >
                                                <FaTimes /> Cancel
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}

                {/* ══ EXPORT TAB (Phase 2 stub) ════════════════════════════ */}
                {activeTab === 'export' && (
                    <div className="aie-export">
                        <div className="aie-coming-soon">
                            <FaFileExport className="aie-coming-soon-icon" />
                            <h2>Export — Coming Soon</h2>
                            <p>
                                Export your full asset register to CSV will be available in the
                                next release.
                            </p>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

export default AssetImportExportPage;